import React, { useState, useEffect } from "react";
import cn from "classnames";

import PrevButton from "components/PrevButton";
import NextButton from "components/NextButton";

import { sendFormError } from "i18n/messages/errors";
import IntlFormattedMessage from "components/IntlFormattedMessage";
import { useFormattedMessage, useSideModalScrollToTop } from "hooks";

import { setMyOrder, orderTan } from "api";
import { RESPONSE_ERRORS } from "constants/index";
import OTPInput from "components/OTPInput";
import NewLoader from "components/NewLoader";

const messages = {
    sendFormError,
    editPurchaseOffer: "edit_purchase_offer",
    boEditOTPHeading: "release_transaction",
    boEditOTPContent: "otp_code",
    boEditOTPResendText: "resend_otp_code",
    boEditOverviewButtonText: "edit_and_publish_purchase",
    invalidOTP: "invalid_otp",
    mobileTANcodeExpired: "mobile_tan_code_expired",
    investmnetChangedInMeantime: "update_your_information",
    sendCodeAgainIn: "send_code_again",
};

const errorsMessagesMapping = {
    invalid_input: <IntlFormattedMessage id={messages.invalidOTP} />,
    TAN_expired: <IntlFormattedMessage id={messages.mobileTANcodeExpired} />,
    "Not enough Balance": "Not enough Balance",
    lowerThan500: "Shares number is lower than 500",
};

const OTP = ({ className = "", onBack, onForward, setCloseTitle, stepData, backSlideEffect = "", fetchInvestmentAmount }) => {
    useSideModalScrollToTop();

    const { formatMessage } = useFormattedMessage();
    const [errorMessage, setErrorMessage] = useState("");
    const [errorCode, setErrorCode] = useState("");
    const [isSubmitting, setSubmitting] = useState(false);
    const [isSendingCode, setSendingCode] = useState(false);
    const [OTP, setOTP] = useState("");
    const [counter, setCounter] = useState(0);

    useEffect(() => {
        setCloseTitle(formatMessage(messages.editPurchaseOffer));
    }, []);

    useEffect(() => {
        setErrorMessage("");
        setErrorCode("");
    }, [OTP]);

    const onResendOtp = () => {
        try {
            setSendingCode(true);
            const orderId = stepData?.ID;
            orderTan(orderId)
                .then((res) => {
                    res && setSendingCode(false);
                    setCounter(90);
                    if (res.data.errors) {
                        setErrorMessage(formatMessage(messages.sendFormError));
                    }
                })
                .catch(() => {
                    setErrorMessage(formatMessage(messages.sendFormError));
                    setSendingCode(false);
                });
        } catch (error) {
            setErrorMessage(formatMessage(messages.sendFormError));
            setSendingCode(false);
        }
    };

    const onSubmit = () => {
        try {
            setSubmitting(true);

            const variables = {
                ...stepData,
                Tan: OTP,
            };

            setMyOrder(variables)
                .then((res) => {
                    res && setSubmitting(false);

                    if (res.data.errors) {
                        const error = res.data.errors[0].message;
                        setErrorCode(error);

                        if (error === RESPONSE_ERRORS.SELF_DECLARATION_ERROR) {
                            setErrorMessage(formatMessage(messages.investmnetChangedInMeantime));
                            fetchInvestmentAmount?.();
                        } else if (errorsMessagesMapping[error]) {
                            setErrorMessage(errorsMessagesMapping[error]);
                        } else {
                            setErrorMessage(formatMessage(messages.sendFormError));
                        }
                        return;
                    }

                    const order = res?.data?.data?.setMyOrder?.order;

                    return onForward(order);
                })
                .catch(() => {
                    setErrorMessage(formatMessage(messages.sendFormError));
                    setSubmitting(false);
                });
        } catch (error) {
            setErrorMessage(formatMessage(messages.sendFormError));
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (counter > 0 && !isSendingCode) {
            const interval = setInterval(() => {
                setCounter(counter - 1);
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [isSendingCode, counter]);

    return (
        <>
            <div className={`order-otp ${className} ${backSlideEffect}`}>
                <h3>{formatMessage(messages.boEditOTPHeading)}</h3>
                <p>{formatMessage(messages.boEditOTPContent)}</p>
                <OTPInput
                    isShowAnimation={false}
                    errorCode={errorCode}
                    value={OTP}
                    onChange={(value) => setOTP(value)}
                    isSubmitting={isSubmitting}
                    errorMessage={errorMessage}
                    shouldAutoFocus
                />
                <div className="flex justify-end">
                    <span
                        className={`light-grey-color text-12 mb-5 ${isSendingCode || counter !== 0 ? "" : "cursor-pointer"}`}
                        onClick={() => {
                            isSendingCode || counter !== 0 ? null : onResendOtp();
                        }}
                        disabled={counter !== 0}
                    >
                        {isSendingCode ? (
                            <>
                                <NewLoader type="primary" />
                                {formatMessage(messages.boEditOTPResendText)}
                            </>
                        ) : counter !== 0 ? (
                            `${formatMessage(messages.sendCodeAgainIn)} ${counter} sec`
                        ) : (
                            formatMessage(messages.boEditOTPResendText)
                        )}
                    </span>
                </div>
            </div>

            {!isSubmitting && errorMessage && <span className="error pl-0">{errorMessage}</span>}
            <div className="bottom-bar-container single-button">
                <PrevButton disable={isSubmitting} onClick={onBack} />
                <NextButton
                    title={formatMessage(messages.boEditOverviewButtonText)}
                    className={cn({
                        "mw-100 mr-1": true,
                        "disable-grey": isSubmitting || OTP?.length !== 6,
                    })}
                    onClick={onSubmit}
                    disable={isSubmitting || OTP?.length !== 6}
                    isLoading={isSubmitting}
                />
            </div>
        </>
    );
};

export default OTP;
